import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import NotificationBell from './NotificationBell'

const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
)

const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
)

export default function Navbar() {
  const { user } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setMenuOpen(false)
    navigate('/')
  }

  const linkClass = (path) =>
    `text-sm font-semibold uppercase tracking-wider transition-colors ${
      location.pathname === path
        ? 'text-green-500 border-b-2 border-green-500 pb-0.5'
        : 'text-gray-400 hover:text-gray-900 dark:hover:text-white'
    }`

  const mobileLinkClass = (path) =>
    `block px-4 py-3 text-sm font-bold uppercase tracking-widest transition-colors border-b border-gray-100 dark:border-white/5 ${
      location.pathname === path
        ? 'text-green-500 bg-green-50 dark:bg-green-900/10'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
    }`

  return (
    <nav className="bg-white dark:bg-black border-b border-black/10 dark:border-white/10 relative z-40">
      <div className="flex justify-between items-center px-6 py-4">

        {/* Logo */}
          <Link
            to={user ? '/dashboard' : '/'}
            className="flex items-center gap-3"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/Mufulira_Wanderers_logo.png" alt="Mufulira Wanderers" className="w-8 h-8 object-contain" />
            <span className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-lg">
              MWFC
            </span>
          </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-6">
          {user ? (
            <>
              <Link to="/dashboard" className={linkClass('/dashboard')}>Dashboard</Link>
              <Link to="/shop" className={linkClass('/shop')}>Shop</Link>
              <Link to="/fixtures" className={linkClass('/fixtures')}>Fixtures</Link>
              <Link to="/welfare" className={linkClass('/welfare')}>Welfare</Link>
              <Link to="/membership-card" className={linkClass('/membership-card')}>My Card</Link>
              <Link to="/profile" className={linkClass('/profile')}>Profile</Link>
              <NotificationBell />
              <button onClick={toggleTheme} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                {isDark ? <SunIcon /> : <MoonIcon />}
              </button>
              <button
                onClick={handleLogout}
                className="border border-black/20 dark:border-white/20 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button onClick={toggleTheme} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                {isDark ? <SunIcon /> : <MoonIcon />}
              </button>
              <Link to="/shop" className="border border-gray-300 dark:border-white/20 text-gray-500 dark:text-gray-400 hover:border-green-600 hover:text-green-600 dark:hover:text-green-400 px-5 py-2 text-sm font-bold uppercase tracking-widest transition-colors">
                Shop
              </Link>
              <Link to="/register" className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 text-sm font-bold uppercase tracking-widest transition-colors">
                Join Now
              </Link>
            </>
          )}
        </div>

        {/* Mobile Right Side */}
        <div className="flex lg:hidden items-center gap-2">
          {user && <NotificationBell />}
          <button onClick={toggleTheme} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {menuOpen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {menuOpen && (
        <div className="lg:hidden bg-white dark:bg-black border-t border-gray-100 dark:border-white/10 pb-2">
          {user ? (
            <>
              <Link to="/dashboard" className={mobileLinkClass('/dashboard')} onClick={() => setMenuOpen(false)}>Dashboard</Link>
              <Link to="/shop" className={mobileLinkClass('/shop')} onClick={() => setMenuOpen(false)}>Shop</Link>
              <Link to="/fixtures" className={mobileLinkClass('/fixtures')} onClick={() => setMenuOpen(false)}>Fixtures</Link>
              <Link to="/welfare" className={mobileLinkClass('/welfare')} onClick={() => setMenuOpen(false)}>Welfare</Link>
              <Link to="/membership-card" className={mobileLinkClass('/membership-card')} onClick={() => setMenuOpen(false)}>My Card</Link>
              <Link to="/profile" className={mobileLinkClass('/profile')} onClick={() => setMenuOpen(false)}>Profile</Link>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-3 text-sm font-bold uppercase tracking-widest text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/shop" className={mobileLinkClass('/shop')} onClick={() => setMenuOpen(false)}>Shop</Link>
              <div className="px-4 py-3">
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="block w-full text-center bg-green-600 hover:bg-green-700 text-white py-3 text-sm font-bold uppercase tracking-widest transition-colors"
                >
                  Join Now
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </nav>
  )
}